/**
 * GalleryCard — 3:4 aspect ratio gallery tile (Phase 4).
 *
 * Figma: 318:4727 (default state).
 *
 * Variants (controlled by `item.type` + `item.status`):
 *  - photo:        static <img>, no overlay
 *  - video done:   <img thumb> + play overlay icon center
 *  - processing:   disabled dark card + spinner + «Генерируем…» label
 *  - failed:       centered alert-circle + «Ошибка генерации»
 *                  (Figma 40000032:6436) — no preview, no play overlay.
 *
 * Props:
 *  - item   ({ id, type, thumbnailUrl, imageUrl, videoUrl, status, ... })
 *  - onClick (fn(item)) — open viewer; ignored when processing
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../primitives';
import { hapticImpact } from '../../utils/haptics';

function FallbackBox({ children }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-2">
      {children}
    </div>
  );
}

function ProcessingState({ label }) {
  return (
    <FallbackBox>
      <div
        className="flex flex-col items-center justify-center gap-3 text-text-primary"
        data-testid="processing-state"
      >
        <Icon
          name="loader"
          size={34}
          className="animate-spin"
          aria-hidden="true"
        />
        <span
          className="font-sans text-[12px] font-semibold leading-none text-text-secondary"
          data-testid="processing-label"
        >
          {label}…
        </span>
      </div>
    </FallbackBox>
  );
}

function FailedState({ label }) {
  return (
    <FallbackBox>
      <div
        className="flex flex-col items-center justify-center gap-3 px-3 text-center text-[#FF4B4B]"
        data-testid="failed-state"
      >
        <svg
          viewBox="0 0 24 24"
          width={34}
          height={34}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="7" x2="12" y2="12.5" />
          <circle cx="12" cy="16.5" r="0.8" fill="currentColor" />
        </svg>
        <span
          className="max-w-full break-words font-sans text-[11px] font-semibold leading-tight"
          data-testid="failed-label"
        >
          {label}
        </span>
      </div>
    </FallbackBox>
  );
}

export default function GalleryCard({ item, onClick }) {
  const { t } = useTranslation();
  const cardRef = useRef(null);

  const isPhoto = item?.type === 'photo';
  const isVideo = item?.type === 'video';
  const status = item?.status || (isPhoto ? 'COMPLETED' : item?.status);

  const isProcessing = status === 'PENDING' || status === 'PROCESSING';
  const isFailed = status === 'FAILED';

  const previewUrl =
    item?.thumbnailUrl ||
    (isPhoto ? item?.imageUrl : null) ||
    item?.srcImageUrl ||
    null;
  const [shouldLoadPreview, setShouldLoadPreview] = useState(() => (
    !previewUrl ||
    typeof window === 'undefined' ||
    typeof window.IntersectionObserver === 'undefined'
  ));

  useEffect(() => {
    if (!previewUrl) {
      setShouldLoadPreview(true);
      return undefined;
    }
    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      setShouldLoadPreview(true);
      return undefined;
    }

    setShouldLoadPreview(false);
    const node = cardRef.current;
    if (!node) {
      setShouldLoadPreview(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoadPreview(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [previewUrl]);

  if (!item) return null;

  const handleClick = () => {
    if (isProcessing || isFailed) return;
    hapticImpact('light');
    if (typeof onClick === 'function') onClick(item);
  };

  return (
    <button
      type="button"
      ref={cardRef}
      onClick={handleClick}
      disabled={isProcessing || isFailed}
      data-testid={`gallery-card-${item.id}`}
      data-card-type={item.type}
      data-card-status={status}
      className="relative w-full overflow-hidden rounded-home-16 bg-surface-2"
      style={{ aspectRatio: '3 / 4' }}
      aria-label={
        isProcessing
          ? t('gallery.generating')
          : isFailed
          ? t('gallery.generationError')
          : isPhoto
          ? t('gallery.openPhoto')
          : t('gallery.openVideo')
      }
    >
      {isProcessing ? (
        <ProcessingState label={t('gallery.generating')} />
      ) : isFailed ? (
        <FailedState label={item.errorMessage || t('gallery.generationError')} />
      ) : previewUrl && shouldLoadPreview ? (
        <img
          src={previewUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <FallbackBox>
          <Icon
            name={isVideo ? 'video' : 'image'}
            size={32}
            className="text-text-tertiary"
          />
        </FallbackBox>
      )}

      {isVideo && !isProcessing && !isFailed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-full"
            style={{ background: 'rgba(0,0,0,0.4)' }}
            data-testid="play-overlay"
          >
            <Icon name="play" size={20} className="text-text-primary" />
          </div>
        </div>
      )}

    </button>
  );
}
